/**
 * timeCrystalPipeline — patterns that repeat in time but not in space.
 *
 * A time crystal is a phase of matter that breaks time-translation
 * symmetry: its structure oscillates in time without any external
 * driving force. We visualize this as a pattern of points that
 * pulse at a fixed period, even though the underlying system
 * has no preferred time scale.
 *
 * Single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface TimeCrystalPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  float r = length(p);
  vec3 bg = mix(
    vec3(0.04, 0.06, 0.16),
    vec3(0.01, 0.01, 0.04),
    smoothstep(0.0, 0.7, r)
  );

  vec3 col = bg;

  // Time crystal lattice — a grid of points that pulse in time.
  float latticeSize = 0.10;
  vec2 q = p / latticeSize;
  vec2 cell = floor(q);
  vec2 local = fract(q) - 0.5;

  // Each cell pulses with a fixed period (the time-crystal
  // period). The pulse is a function of t, not of any driving force.
  float period = 0.8 + 0.2 * sin(cell.x * 0.5 + cell.y * 0.7);
  float phase = uTime / period;
  float pulse = 0.5 + 0.5 * sin(phase * 6.28);
  // The pulse makes the cell glow brighter.
  float cellGlow = exp(-dot(local, local) * 8.0) * pulse;
  col += vec3(0.4, 0.85, 1.0) * cellGlow * (0.5 + 0.5 * mid);

  // Cell centers are bright dots.
  float dotMask = exp(-dot(local, local) * 100.0);
  col += vec3(0.95, 0.95, 0.7) * dotMask * pulse * 0.7;

  // A "phase" line — connects cells that are at the same phase
  // in their oscillation. This shows the "time crystal" structure.
  float phaseGrid = 0.0;
  vec2 phaseDir = vec2(0.0);
  // Sample the phase at neighboring cells.
  for (int k = 0; k < 4; k++) {
    float fk = float(k);
    vec2 offset = vec2(cos(fk * 1.5708), sin(fk * 1.5708));
    vec2 neighbor = floor(q + offset);
    float neighborPeriod = 0.8 + 0.2 * sin(neighbor.x * 0.5 + neighbor.y * 0.7);
    float neighborPhase = uTime / neighborPeriod;
    if (abs(pulse - sin(neighborPhase * 6.28) * 0.5 - 0.5) < 0.1) {
      // Same phase — connect them.
      float lineD = 1e9;
      for (int l = 0; l < 10; l++) {
        float fl = float(l) / 9.0;
        vec2 pt = cell * latticeSize + offset * latticeSize * fl;
        float d = length(p - pt);
        lineD = min(lineD, d);
      }
      phaseGrid = max(phaseGrid, exp(-lineD * 200.0));
    }
  }
  col += vec3(0.95, 0.55, 0.85) * phaseGrid * 0.4;

  // Time-crystal "clock" — a small circle showing the period.
  vec2 clockPos = vec2(-0.78, 0.45);
  float clockD = length(p - clockPos);
  float clockRing = 1.0 - smoothstep(0.0, 0.03, abs(clockD - 0.05));
  col += vec3(0.9, 0.7, 0.4) * clockRing * 0.6;
  // Hand of the clock.
  vec2 handPos = clockPos + vec2(cos(phase * 6.28), sin(phase * 6.28)) * 0.05;
  float handD = length(p - handPos);
  float handMask = 1.0 - smoothstep(0.0, 0.005, handD);
  col += vec3(0.95, 0.95, 0.7) * handMask * 1.0;

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;

  outColor = vec4(col, 1.0);
}
`;

export function computeTimeCrystalBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeTimeCrystalRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createTimeCrystalPipeline(gl: WebGL2RenderingContext): TimeCrystalPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawTimeCrystal(
  gl: WebGL2RenderingContext,
  pipe: TimeCrystalPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
